import { useEffect } from "react";
import { useLocation } from "react-router-dom";

interface SchemaOrgProps {
  type: "Organization" | "WebSite" | "WebPage" | "BreadcrumbList" | "Article" | "FAQPage";
  data: Record<string, unknown>;
}

function SchemaOrg({ data }: { data: Record<string, unknown> }) {
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }}
    />
  );
}

function getBaseUrl() {
  return "https://www.muguays.com";
}

export function OrganizationSchema() {
  const data = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "木瓜影视",
    url: getBaseUrl(),
    description:
      "木瓜影视提供高清不缓冲在线观看体验，不卡顿无广告极速播放，涵盖最新电视剧和电影高清免费在线看，畅享流畅观影。",
    foundingDate: "2020-01-01",
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "customer service",
      email: "feedback@muguays.com",
    },
  };

  return <SchemaOrg data={data} />;
}

export function WebsiteSchema() {
  const data = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "木瓜影视",
    url: getBaseUrl(),
    description:
      "高清不缓冲在线观看平台，免费无广告极速播放，最新电视剧电影高清免费在线看。",
    potentialAction: {
      "@type": "SearchAction",
      "target": `${getBaseUrl()}/search?q={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
  };

  return <SchemaOrg data={data} />;
}

export function WebPageSchema({
  title,
  description,
  url,
  dateModified,
}: {
  title: string;
  description: string;
  url: string;
  dateModified?: string;
}) {
  const data: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: title,
    description,
    url,
  };

  if (dateModified) {
    data.dateModified = dateModified;
  }

  return <SchemaOrg data={data} />;
}

export function BreadcrumbListSchema({
  items,
}: {
  items: { name: string; url: string }[];
}) {
  const data = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };

  return <SchemaOrg data={data} />;
}

export function ArticleSchema({
  title,
  description,
  url,
  datePublished,
  author,
  imageUrl,
}: {
  title: string;
  description: string;
  url: string;
  datePublished: string;
  author: string;
  imageUrl?: string;
}) {
  const data: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: title,
    description,
    url,
    datePublished,
    author: {
      "@type": "Person",
      name: author,
    },
    publisher: {
      "@type": "Organization",
      name: "木瓜影视",
    },
  };

  if (imageUrl) {
    data.image = imageUrl;
  }

  return <SchemaOrg data={data} />;
}

export function FAQPageSchema({
  questions,
}: {
  questions: { question: string; answer: string }[];
}) {
  const data = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: questions.map((q) => ({
      "@type": "Question",
      name: q.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: q.answer,
      },
    })),
  };

  return <SchemaOrg data={data} />;
}

export function useSEO({
  title,
  description,
  keywords,
  canonicalPath,
  ogImage,
  ogType = "website",
  articleData,
  faqData,
  breadcrumbItems,
}: {
  title?: string;
  description?: string;
  keywords?: string;
  canonicalPath?: string;
  ogImage?: string;
  ogType?: string;
  articleData?: {
    publishedTime: string;
    author: string;
  };
  faqData?: { question: string; answer: string }[];
  breadcrumbItems?: { name: string; url: string }[];
}) {
  const location = useLocation();
  const baseUrl = getBaseUrl();
  const fullUrl = `${baseUrl}${canonicalPath || location.pathname}`;
  const pageTitle = title
    ? `${title} - 木瓜影视`
    : "木瓜影视 - 高清不缓冲在线观看不卡顿：免费无广告极速播放";

  useEffect(() => {
    document.title = pageTitle;

    const setMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? "property" : "name";
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    setMeta("description", description || "木瓜影视提供高清不缓冲在线观看体验，不卡顿无广告极速播放，涵盖最新电视剧和电影高清免费在线看，畅享流畅观影。");
    setMeta("keywords", keywords || "木瓜影视,高清不缓冲,在线观看不卡顿,免费无广告,极速播放,高清影视,最新电视剧,最新电影");

    const canonicalEl = document.querySelector('link[rel="canonical"]');
    if (canonicalEl) {
      canonicalEl.setAttribute("href", fullUrl);
    }

    setMeta("og:title", pageTitle, true);
    setMeta("og:description", description || "木瓜影视提供高清不缓冲在线观看体验", true);
    setMeta("og:url", fullUrl, true);
    setMeta("og:type", ogType, true);
    if (ogImage) {
      setMeta("og:image", ogImage, true);
    }

    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", pageTitle);
    setMeta("twitter:description", description || "木瓜影视提供高清不缓冲在线观看体验");
  }, [title, description, keywords, canonicalPath, fullUrl, pageTitle, ogImage, ogType]);

  return {
    OrganizationSchema: <OrganizationSchema />,
    WebsiteSchema: <WebsiteSchema />,
    WebPageSchema: (
      <WebPageSchema
        title={pageTitle}
        description={description || ""}
        url={fullUrl}
      />
    ),
    ...(breadcrumbItems && {
      BreadcrumbListSchema: (
        <BreadcrumbListSchema items={breadcrumbItems} />
      ),
    }),
    ...(articleData && {
      ArticleSchema: (
        <ArticleSchema
          title={pageTitle}
          description={description || ""}
          url={fullUrl}
          datePublished={articleData.publishedTime}
          author={articleData.author}
        />
      ),
    }),
    ...(faqData && {
      FAQPageSchema: <FAQPageSchema questions={faqData} />,
    }),
  };
}